import AddIcon from "@mui/icons-material/Add";
import { Checkbox, CircularProgress, InputBase, ListItem, ListItemText, Popper, Stack } from "@mui/material";
import React, { forwardRef, useEffect, useRef, useState } from "react";

import { GroundlightButton } from "new-components/buttons/GroundlightButton";
import { Typography } from "new-components/typography/Typography";

import { GL_BRIGHT_COLOR } from "../constants";
import { OptionType } from "../types";
import { getOptionData } from "../utils";

import { Badge } from "./Badge";
import { MenuPaper } from "./MenuPaper";
import { OptionMenuItem } from "./OptionMenuItem";

export interface MenuProps {
  menuOpen: boolean;
  options: OptionType[];
  handleOptionSelect: (option: OptionType) => void;
  containerRef: React.RefObject<HTMLDivElement>;
  selectedValue: OptionType | null;
  selectedValues?: OptionType[];
  multiple?: boolean;
  isLoadingOptions?: boolean;
  isFetchingNextPage?: boolean;
  hasMorePages?: boolean;
  fetchNextPage?: () => void;
  onCreateOption?: (value: string) => Promise<OptionType | undefined>;
  isCreating?: boolean;
  setIsCreating?: (isCreating: boolean) => void;
  handleNewSelectedOptions: (newSelectedOptions: OptionType[]) => void;
}

export const Menu = forwardRef(
  (
    {
      menuOpen,
      options,
      handleOptionSelect,
      containerRef,
      selectedValue,
      selectedValues = [],
      multiple = false,
      isLoadingOptions = false,
      isFetchingNextPage = false,
      hasMorePages = false,
      fetchNextPage,
      onCreateOption,
      isCreating = false,
      setIsCreating,
      handleNewSelectedOptions,
    }: MenuProps,
    ref: React.Ref<HTMLDivElement>
  ): JSX.Element | null => {
    const createInputRef = useRef<HTMLInputElement>(null);
    const [newOptionValue, setNewOptionValue] = useState<string>("");
    const [highlightedOptionValue, setHighlightedOptionValue] = useState<string | null>(null);
    const showCreateOption = onCreateOption && !isCreating;

    const handleCreateClick = () => {
      setIsCreating?.(true);
    };

    const handleCreateKeyDown = async (e: React.KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        setIsCreating?.(false);
        setNewOptionValue("");
        return;
      }

      if (e.key === "Enter") {
        e.preventDefault();
        if (onCreateOption && newOptionValue !== "") {
          const newOption = await onCreateOption(newOptionValue);

          if (newOption) {
            setHighlightedOptionValue(newOption.value);
          }

          setIsCreating?.(false);
          setNewOptionValue("");
        }
      }
    };

    useEffect(() => {
      if (isCreating) {
        createInputRef.current?.focus();
      }
    }, [isCreating]);

    useEffect(() => {
      if (!highlightedOptionValue) {
        return () => {};
      }

      const timer = setTimeout(() => {
        setHighlightedOptionValue(null);
      }, 3_000);

      return () => clearTimeout(timer);
    }, [highlightedOptionValue]);

    // Menu is only rendered when menuOpen is true and variant is select/autofill
    if (!menuOpen) return null;

    return (
      <Popper
        open
        anchorEl={containerRef.current}
        placement="bottom-start"
        sx={{ width: containerRef.current?.offsetWidth, zIndex: 1300 }}
        ref={ref}
      >
        <MenuPaper elevation={4}>
          {!isLoadingOptions && options.length === 0 && !showCreateOption && !isCreating && (
            <OptionMenuItem disabled>No results found...</OptionMenuItem>
          )}

          {showCreateOption && (
            <OptionMenuItem onClick={handleCreateClick} role="button">
              <Stack
                direction="row"
                alignItems="center"
                sx={{
                  color: "#0D297D",
                  fontWeight: 500,
                }}
                gap={1}
              >
                <AddIcon />
                <ListItemText primary="Create new" />
              </Stack>
            </OptionMenuItem>
          )}

          {isCreating && (
            <ListItem
              sx={{
                height: "44px",
              }}
            >
              <InputBase
                fullWidth
                placeholder=""
                value={newOptionValue}
                onChange={(e) => setNewOptionValue(e.target.value)}
                onKeyDown={handleCreateKeyDown}
                inputRef={createInputRef}
                autoFocus
                inputProps={{ "data-testid": "creation-input" }}
              />
            </ListItem>
          )}

          {(!isLoadingOptions || isFetchingNextPage) &&
            options.length > 0 &&
            options.map((option) => {
              const {
                value: optionValue,
                label: optionLabel,
                badge: optionBadge,
                disabled: optionDisabled,
              } = getOptionData(option);

              // Check if this option is selected (single or multiple selection)
              let isSelected: boolean;
              if (multiple) {
                isSelected = selectedValues.some((selected) => getOptionData(selected).value === optionValue);
              } else {
                isSelected = selectedValue ? optionValue === selectedValue.value : false;
              }
              const isHighlighted = optionValue === highlightedOptionValue;

              return (
                <OptionMenuItem
                  key={optionValue}
                  onClick={() => handleOptionSelect(option)}
                  selected={isSelected || isHighlighted}
                  disabled={optionDisabled}
                >
                  {multiple && (
                    <Checkbox
                      checked={isSelected}
                      disabled={optionDisabled}
                      size="small"
                      sx={{
                        padding: 0,
                        marginRight: "12px",
                        "& .MuiSvgIcon-root": {
                          fontSize: "16px",
                        },
                      }}
                      onClick={() => {
                        handleOptionSelect(option);
                      }}
                    />
                  )}
                  <Typography noWrap sx={{ flex: 1 }}>
                    {optionLabel}
                  </Typography>
                  {optionBadge && <Badge disabled={optionDisabled}>{optionBadge}</Badge>}
                </OptionMenuItem>
              );
            })}

          {isLoadingOptions && !isFetchingNextPage && (
            <OptionMenuItem>
              <Typography
                sx={{
                  width: "100%",
                  textAlign: "center",
                  color: "#666",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                Loading...
              </Typography>
            </OptionMenuItem>
          )}

          {!isLoadingOptions && hasMorePages && options.length > 0 && (
            <OptionMenuItem
              disabled={isFetchingNextPage}
              onClick={fetchNextPage}
              sx={{
                color: GL_BRIGHT_COLOR,
                cursor: isFetchingNextPage ? "default" : "pointer",
                justifyContent: "center",
              }}
            >
              {isFetchingNextPage ? <CircularProgress size={16} /> : "Load more"}
            </OptionMenuItem>
          )}

          {isFetchingNextPage && (
            <OptionMenuItem disabled>
              <CircularProgress size={16} sx={{ mx: "auto" }} />
            </OptionMenuItem>
          )}

          {multiple && (
            <Stack
              gap={1.5}
              direction="row"
              justifyContent="center"
              padding={1.25}
              sx={{ borderTop: "1px solid #C8CDD3", position: "sticky", bottom: 0, background: "white" }}
            >
              <GroundlightButton
                variant="border"
                onClick={() => {
                  handleNewSelectedOptions([]);
                }}
              >
                Clear all
              </GroundlightButton>

              <GroundlightButton
                variant="border"
                onClick={() => {
                  handleNewSelectedOptions(options);
                }}
              >
                Select all
              </GroundlightButton>
            </Stack>
          )}
        </MenuPaper>
      </Popper>
    );
  }
);
